import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { scrollToId } from "@/lib/smooth-scroll";
import { BrandLogo } from "@/components/site/BrandLogo";

gsap.registerPlugin(ScrollTrigger);

const COLUMNS = [
  {
    heading: "Leistungen",
    links: [
      { label: "KI-Chatbots & Assistenten", target: "#leistungen" },
      { label: "Prozess-Automatisierung", target: "#leistungen" },
      { label: "Webseiten & Auftritt", target: "#leistungen" },
      { label: "Workshops & Schulungen", target: "#leistungen" },
    ],
  },
  {
    heading: "Unternehmen",
    links: [
      { label: "Unser Ansatz", target: "#ansatz" },
      { label: "Branchen", target: "#branchen" },
      { label: "Kontakt", target: "#kontakt" },
    ],
  },
  {
    heading: "Rechtliches",
    links: [
      { label: "Impressum", href: "/impressum" },
      { label: "Datenschutz", href: "/datenschutz" },
    ],
  },
];

export const Footer = () => {
  const footRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".footer-inner",
        { y: 70, opacity: 0.25 },
        {
          y: 0,
          opacity: 1,
          ease: "none",
          scrollTrigger: {
            trigger: footRef.current,
            start: "top bottom",
            end: "top 65%",
            scrub: true,
          },
        }
      );
    }, footRef);
    return () => ctx.revert();
  }, []);

  return (
    <footer
      ref={footRef}
      className="relative bg-[#2B2825]"
      data-dark="true"
      style={{ borderTop: "1px solid rgba(212, 169, 92, 0.3)" }}
      data-testid="footer"
    >
      <div className="footer-inner mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <div className="grid gap-14 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <button
              onClick={() => scrollToId("#hero")}
              className="flex items-center bg-transparent border-none p-0"
              aria-label="Nach oben"
              data-testid="footer-brand"
            >
              <BrandLogo className="h-10 w-auto" invert />
              <span className="sr-only">DirektLab</span>
            </button>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-[#F5F1E8]/55">
              Praktische KI-Lösungen für Praxen, Kanzleien und Betriebe —
              persönlich betreut, verständlich erklärt.
            </p>
          </div>
          {COLUMNS.map((col) => (
            <div key={col.heading}>
              <h4 className="eyebrow mb-6 text-[#E8C887]/80">{col.heading}</h4>
              <ul className="space-y-3.5">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.href ? (
                      <Link
                        to={link.href}
                        className="footer-link inline-block no-underline"
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <button
                        className="footer-link"
                        onClick={() => link.target && scrollToId(link.target)}
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      >
                        {link.label}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-16 flex flex-col gap-3 border-t border-[#F5F1E8]/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-[#6E685F]">
            © 2026 DirektLab. Alle Rechte vorbehalten.
          </p>
          <p className="text-xs text-[#6E685F]">
            Mit Sorgfalt gebaut — für den Mittelstand.
          </p>
        </div>
      </div>
    </footer>
  );
};
